import { debounce, reduce } from "lodash/fp";

import Config from "@readai/common/config";
import logger from "@readai/common/logger";
import { isResponseErrorWithStatus } from "@readai/common/utils";
import { camelizeKeys, snakeKeys } from "@readai/common/utils/formatUtils";

import getFetchStrategy, { FetchStrategy } from "./fetchStrategy";
import {
  X_READ_APP_INFO,
  X_READ_APP_LOCATION,
  X_READ_APP_USER_ID,
} from "./headers";
import ResponseError from "./responseError";
import type { ApiOptions, PostApiOptions, ResponseHeaders } from "./types";

const PROTOCOL_REGEX = /^https?:\/\//;

const getUrlFromPath = (path: string) =>
  path.match(PROTOCOL_REGEX) ? path : `${Config.api.HOST}${path}`;

const dispatchRefreshTokenFailed = debounce(500, () => {
  window.dispatchEvent(new Event("refresh_token_failed"));
});

let tokenRefreshPromise: Promise<boolean> | null = null;
let wasLastRefreshAttemptSuccessful = true;
let lastRefreshAttemptFailureMillis = 0;

const attemptTokenRefresh = async (): Promise<boolean> => {
  if (tokenRefreshPromise) {
    return tokenRefreshPromise;
  } else if (new Date().valueOf() - lastRefreshAttemptFailureMillis < 2000) {
    // in the case a failure has happened in the last couple seconds, let's not re-attempt
    // this can happen while we are waiting for the page to be navigated back into sign-in
    // if the token request was not queued alongside the first refresh

    return wasLastRefreshAttemptSuccessful;
  }

  tokenRefreshPromise = (async () => {
    try {
      await rawPost("/users/me/token", {}, { withCreds: true });
      wasLastRefreshAttemptSuccessful = true;
    } catch (e) {
      logger.info("failed to refresh access token, please re-authenticate", e);
      if (typeof window !== "undefined") {
        dispatchRefreshTokenFailed();
      }
      wasLastRefreshAttemptSuccessful = false;
      lastRefreshAttemptFailureMillis = new Date().valueOf();
    } finally {
      tokenRefreshPromise = null;
    }

    return wasLastRefreshAttemptSuccessful;
  })();

  return tokenRefreshPromise;
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const tokenRetry =
  (httpCall: (...args: any) => Promise<any>) =>
  async (...args: any) => {
    try {
      return await httpCall(...args);
    } catch (error) {
      if (isResponseErrorWithStatus(error, 401)) {
        // refresh token, if success, then retry, otherwise fail
        // with original error
        logger.info("attempting to refresh access token...");
        const refreshSuccess = await attemptTokenRefresh();
        if (refreshSuccess) {
          return await httpCall(...args);
        }
      }
      throw error;
    }
  };
/* eslint-enable @typescript-eslint/no-explicit-any */

// TODO: pass through httpcall types
/* eslint-disable @typescript-eslint/no-explicit-any */
const withFetch = <F extends (...args: any[]) => any>(
  fetchFn: typeof fetch,
  httpCall: F,
) => {
  return async (...args: any[]) => {
    return httpCall(...args, fetchFn);
  };
};
/* eslint-enable @typescript-eslint/no-explicit-any */

let readAppInfoHeader: string | undefined;
let readAppUserIdHeader: string | undefined;

interface PackageLike {
  name: string;
  version: string;
}

/**
 * Sets the active user ID header to be used in API requests.
 *
 * This header includes the user ID of the currently active user.
 * It is used to provide metadata about the user making the request.
 *
 * @param userId - The user ID of the currently active user.
 */
export const setActiveUserId = (userId?: string) => {
  readAppUserIdHeader = userId;
};

/**
 * Sets the application information header to be used in API requests.
 *
 * This header includes the name and version of the application, formatted as "name:v{version}".
 * It is used to provide metadata about the application making the request.
 *
 * @param packageLike - An object containing the name and version of the application.
 * @param packageLike.name - The name of the application.
 * @param packageLike.version - The version of the application.
 */
export const setAppInfoHeader = (packageLike: PackageLike) => {
  readAppInfoHeader = `${packageLike.name}:v${packageLike.version}`;
};

const getHeaders = (options: ApiOptions, includeJSONHeader = true) => ({
  ...(readAppInfoHeader ? { [X_READ_APP_INFO]: readAppInfoHeader } : {}),
  ...(readAppUserIdHeader ? { [X_READ_APP_USER_ID]: readAppUserIdHeader } : {}),
  ...(includeJSONHeader ? { "Content-Type": "application/json" } : {}),
  [X_READ_APP_LOCATION]: location.href,
  ...(options.authBearer && {
    Authorization: `Bearer ${options.authBearer}`,
  }),
});

// tokenRetry uses this method to make the refresh attempt,
// we don't want the call that makes the refresh attempt, to make
// a refresh attempt if it fails :)
export const rawPost = async (
  path: string,
  data: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  options: PostApiOptions = {
    withCreds: false,
    authBearer: undefined,
    parseErrorResponse: false,
  },
  fetchFn: typeof fetch = fetch,
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "POST",
    headers: getHeaders(options),
    body: JSON.stringify(snakeKeys(data)),
    credentials: options.withCreds ? "include" : "omit",
  });

  let responseHeaders;
  if (options.returnResponseHeaders) {
    responseHeaders = reduce<string, ResponseHeaders>(
      (headers, header) => {
        headers[header] = response.headers.get(header);
        return headers;
      },
      {},
      options.returnResponseHeaders,
    );
  }

  // If no success body, return the headers if set in options or undefined.
  if (response.status === 204) return responseHeaders;

  const shouldParseError =
    options.parseErrorResponse &&
    (!options.parseErrorResponseStatuses ||
      options.parseErrorResponseStatuses.includes(response.status));

  if (!response.ok && !shouldParseError) {
    throw new ResponseError(response);
  }

  try {
    const json = await response.json();
    const camelizedJson = camelizeKeys(json);

    // Leave the header keys unmodified, to enable parsing with the same header response keys.
    if (responseHeaders) camelizedJson.responseHeaders = responseHeaders;

    return camelizedJson;
  } catch (_error) {
    logger.warn("json could not be parsed from response body");
    return;
  }
};

export const rawGet = async <T>(
  path: string,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch,
): Promise<T | null> => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "GET",
    headers: getHeaders(options, false),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  if (response.status === 204) return null; // handle success with no body

  try {
    const json = await response.json();

    return camelizeKeys(json) as T;
  } catch (_error) {
    logger.info("json could not be parsed from response body");
    return null;
  }
};

export const rawPut = async (
  path: string,
  data: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  options: ApiOptions = {
    withCreds: false,
    authBearer: undefined,
    parseErrorResponse: false,
  },
  fetchFn: typeof fetch = fetch,
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "PUT",
    headers: getHeaders(options),
    body: JSON.stringify(snakeKeys(data)),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204) return; // handle success with no body

  if (!response.ok && !options.parseErrorResponse) {
    throw new ResponseError(response);
  }

  try {
    const json = await response.json();
    return camelizeKeys(json);
  } catch (_error) {
    logger.warn("json could not be parsed from response body");
    return;
  }
};

export const rawPatch = async (
  path: string,
  data: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch,
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "PATCH",
    headers: getHeaders(options),
    body: JSON.stringify(snakeKeys(data)),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204 || response.status === 202) return true; // handle success with no body

  if (!response.ok) {
    throw new ResponseError(response);
  }

  const json = await response.json();
  return camelizeKeys(json);
};

export const rawDelete = async (
  path: string,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  data?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  fetchFn: typeof fetch = fetch,
): Promise<boolean> => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "DELETE",
    headers: getHeaders(options),
    ...(data && {
      body: JSON.stringify(snakeKeys(data)),
    }),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (response.status === 204) return true; // handle success with no body

  if (!response.ok) {
    throw new ResponseError(response);
  }

  return true;
};

export const rawUploadFile = async (
  path: string,
  file: File,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch,
) => {
  const data = new FormData();
  data.append("file", file);

  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "PUT",
    headers: getHeaders(options, false),
    body: data,
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  return Promise.resolve();
};

export const rawUploadFiles = async (
  path: string,
  files: { [key: string]: File },
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch,
): Promise<void> => {
  const data = new FormData();
  Object.entries(files).forEach(([key, file]) => {
    data.append(key, file);
  });

  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "POST",
    headers: getHeaders(options, false),
    body: data,
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  return Promise.resolve();
};

export const rawGetBlobUrl = async (
  path: string,
  options: ApiOptions = { withCreds: false, authBearer: undefined },
  fetchFn: typeof fetch = fetch,
) => {
  const url = getUrlFromPath(path);
  const response = await fetchFn(url, {
    method: "GET",
    headers: getHeaders(options, false),
    credentials: options.withCreds ? "include" : "omit",
  });

  if (!response.ok) {
    throw new ResponseError(response);
  }

  try {
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (_error) {
    logger.info("blob could not be generated from response");
    return null;
  }
};

const getApi = (fetchStrategy: FetchStrategy) => {
  const doFetch = getFetchStrategy(fetchStrategy);
  const rawPostWithFetch = withFetch(doFetch, rawPost);
  const rawGetWithFetch = withFetch(doFetch, rawGet);
  const rawPutWithFetch = withFetch(doFetch, rawPut);
  const rawPatchWithFetch = withFetch(doFetch, rawPatch);
  const rawDeleteWithFetch = withFetch(doFetch, rawDelete);
  const rawUploadFileWithFetch = withFetch(doFetch, rawUploadFile);
  const rawUploadFilesWithFetch = withFetch(doFetch, rawUploadFiles);
  const rawGetBlobUrlWithFetch = withFetch(doFetch, rawGetBlobUrl);

  const post = tokenRetry(rawPostWithFetch);
  const get = tokenRetry(rawGetWithFetch);
  const put = tokenRetry(rawPutWithFetch);
  const patch = tokenRetry(rawPatchWithFetch);
  const httpDelete = tokenRetry(rawDeleteWithFetch);
  const uploadFile = tokenRetry(rawUploadFileWithFetch);
  const uploadFiles = tokenRetry(rawUploadFilesWithFetch);
  const getBlobUrl = tokenRetry(rawGetBlobUrlWithFetch);

  return {
    rawPost: rawPostWithFetch,
    rawGet: rawGetWithFetch,
    rawGetBlobUrl: rawGetBlobUrlWithFetch,
    rawPut: rawPutWithFetch,
    rawPatch: rawPatchWithFetch,
    rawDelete: rawDeleteWithFetch,
    rawUploadFile: rawUploadFileWithFetch,
    rawUploadFiles: rawUploadFilesWithFetch,
    post,
    get,
    getBlobUrl,
    put,
    patch,
    httpDelete,
    uploadFile,
    uploadFiles,
  };
};

export default getApi;
